import { Component } from '@theme/component';

class VyleRail extends Component {
  #controller;
  #observer;
  #frame;
  #rail;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    this.#rail = this.querySelector('[data-vr-rail]');
    if (!(this.#rail instanceof HTMLElement)) return;
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', this.#onClick, options);
    this.#rail.addEventListener('scroll', this.#queueUpdate, { ...options, passive: true });
    this.#observer = new ResizeObserver(this.#queueUpdate);
    this.#observer.observe(this.#rail);
    this.#update();
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element) || !this.#rail) return;
    const filter = event.target.closest('[data-vr-filter]');
    if (filter instanceof HTMLButtonElement) {
      const category = filter.dataset.vrFilter;
      for (const button of this.querySelectorAll('[data-vr-filter]')) {
        button.setAttribute('aria-pressed', String(button === filter));
      }
      for (const card of this.#rail.querySelectorAll('[data-vr-category]')) {
        if (card instanceof HTMLElement) card.hidden = category !== 'all' && card.dataset.vrCategory !== category;
      }
      this.#rail.scrollTo({ left: 0, behavior: 'instant' });
      this.#update();
      return;
    }
    const jump = event.target.closest('[data-vr-go]');
    if (jump instanceof HTMLButtonElement) {
      const cards = [...this.#rail.children].filter(card => card instanceof HTMLElement && !card.hidden);
      const card = cards[Number(jump.dataset.vrGo)];
      if (card) this.#rail.scrollTo({ left: card.getBoundingClientRect().left - this.#rail.getBoundingClientRect().left + this.#rail.scrollLeft, behavior: 'instant' });
      this.#queueUpdate(); return;
    }
    const arrow = event.target.closest('[data-vr-direction]');
    if (!(arrow instanceof HTMLButtonElement)) return;
    const rtl = getComputedStyle(this.#rail).direction === 'rtl' ? -1 : 1;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#rail.scrollBy({ left: this.#rail.clientWidth * Number(arrow.dataset.vrDirection) * rtl, behavior: reduced ? 'instant' : 'smooth' });
  };

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    if (!this.#rail) return;
    const cards = [...this.#rail.children].filter(card => card instanceof HTMLElement && !card.hidden);
    const railBox = this.#rail.getBoundingClientRect();
    const rtl = getComputedStyle(this.#rail).direction === 'rtl';
    let index = cards.findIndex(card => {
      const box = card.getBoundingClientRect();
      return rtl ? box.right <= railBox.right + 3 && box.right > railBox.left : box.left >= railBox.left - 3 && box.left < railBox.right;
    });
    if (index < 0) index = 0;
    for (const dot of this.querySelectorAll('[data-vr-go]')) {
      const active = Number(dot.dataset.vrGo) === index;
      dot.classList.toggle('active', active);
      if (active) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
    }
    const status = this.querySelector('[data-vr-status]');
    const message = cards.length ? `${index + 1} / ${cards.length}` : '';
    if (status && status.textContent !== message) status.textContent = message;
    const offset = Math.abs(this.#rail.scrollLeft);
    const limit = this.#rail.scrollWidth - this.#rail.clientWidth;
    const previous = this.querySelector('[data-vr-direction="-1"]');
    const next = this.querySelector('[data-vr-direction="1"]');
    if (previous instanceof HTMLButtonElement) previous.disabled = offset < 3;
    if (next instanceof HTMLButtonElement) next.disabled = offset >= limit - 3;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('vyle-rail-component')) customElements.define('vyle-rail-component', VyleRail);


